import Phaser from 'phaser';
import { FACTION_COLORS } from '../../shared/factions';
import type { FactionId } from '../../shared/types';
import { DAMAGE_NUMBER_STYLE } from './readability';

/**
 * Damage number style. Crit = matching faction → larger, accent color, slightly
 * higher float. Normal = neutral red. Heal = green (future-proofing).
 */
export type DamageKind = 'normal' | 'affinity' | 'heal';

export interface FloatingDamageOptions {
  kind?: DamageKind;
  /** Player/skill faction — when kind='affinity', colors the number with that faction's accent. */
  faction?: FactionId | null;
  /** Pixel distance to float upward before fading out (default 36). */
  riseY?: number;
  /** Tween duration in ms (default 500 per task #19 spec). */
  durationMs?: number;
}

const DEFAULT_RISE_PX = 36;
const DEFAULT_DURATION_MS = 500;

/**
 * Resolve the text fill color for a floating damage number.
 * Pure helper — exported for unit tests that don't want a Phaser scene.
 */
export function damageColorFor(opts: FloatingDamageOptions = {}): string {
  if (opts.kind === 'heal') return '#66bb6a';
  if (opts.kind === 'affinity' && opts.faction) {
    return FACTION_COLORS[opts.faction].accentHex;
  }
  return '#ef5350';
}

/**
 * W1 戰場可讀性:resolve the FULL text style (color + font px) for a damage
 * number, layering the「大傷害更大更黃」rule on top of damageColorFor.
 * Pure helper — exported for unit tests.
 *
 * Priority: affinity(爆擊)keeps its faction accent color but gets the big
 * font; non-affinity hits at/above bigDamageMin go gold + big font.
 */
export function damageStyleFor(
  amount: number,
  opts: FloatingDamageOptions = {},
): { color: string; fontPx: number } {
  const D = DAMAGE_NUMBER_STYLE;
  if (opts.kind === 'affinity') {
    return { color: damageColorFor(opts), fontPx: D.fontPxAffinity };
  }
  if (opts.kind !== 'heal' && amount >= D.bigDamageMin) {
    return { color: D.bigDamageColor, fontPx: D.fontPxBig };
  }
  return { color: damageColorFor(opts), fontPx: D.fontPxNormal };
}

/**
 * Spawn a floating damage number that drifts upward and fades out.
 * Returns the Text object so callers can position-correct or tests can inspect it.
 *
 * Wrapped in a try-guard: if scene.add.text or scene.tweens.add are unavailable
 * (e.g. partial test stub), returns null instead of throwing. Production paths
 * always have both; the guard is for resilience under harness conditions.
 */
export function spawnDamageNumber(
  scene: Phaser.Scene,
  x: number,
  y: number,
  amount: number,
  opts: FloatingDamageOptions = {},
): Phaser.GameObjects.Text | null {
  // W1 戰場可讀性:字級加倍 + 深色描邊 + scale pop(體檢根因 #6 — 18px 手機
  // 縮放後 ~9px 看不見)。數值集中在 readability.ts 的 DAMAGE_NUMBER_STYLE。
  const D = DAMAGE_NUMBER_STYLE;
  const { color, fontPx } = damageStyleFor(amount, opts);
  let txt: Phaser.GameObjects.Text;
  try {
    txt = scene.add.text(x, y, '-' + amount, {
      fontSize: fontPx + 'px', color, fontStyle: 'bold', fontFamily: 'sans-serif',
      stroke: D.strokeColor, strokeThickness: D.strokePx,
    });
    if (typeof (txt as { setOrigin?: unknown }).setOrigin === 'function') txt.setOrigin(0.5);
    if (typeof (txt as { setDepth?: unknown }).setDepth === 'function') txt.setDepth(50);
    // pop:出現時 1.3 → 同一個 tween 的 per-property scale 軌道收回 1.0。
    if (typeof (txt as { setScale?: unknown }).setScale === 'function') txt.setScale(D.popScaleFrom);
  } catch {
    return null;
  }
  try {
    scene.tweens.add({
      targets: txt,
      y: y - (opts.riseY ?? DEFAULT_RISE_PX),
      alpha: 0,
      // scale pop 用 Phaser per-property tween config:短促(popMs)收回 1.0,
      // 與整體 500ms 升起/淡出共用同一個 tween(headless 測試 stub 也只看到 1 個)。
      scale: { value: 1, duration: D.popMs, ease: 'Back.easeOut' },
      duration: opts.durationMs ?? DEFAULT_DURATION_MS,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        try { txt.destroy(); } catch { /* already gone */ }
      },
    });
  } catch {
    try { txt.destroy(); } catch { /* noop */ }
    return null;
  }
  return txt;
}
